import { AutosizeModule } from 'ngx-autosize';
import { AgGridModule } from 'ag-grid-angular';
import * as ngCommon from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppLocalizationService } from '@app/shared/common/localization/app-localization.service';
import { AppNavigationService } from '@app/shared/layout/nav/app-navigation.service';
import { CommonModule } from '@shared/common/common.module';
import { UtilsModule } from '@shared/utils/utils.module';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { AppAuthService } from './auth/app-auth.service';
import { AppRouteGuard } from './auth/auth-route-guard';
import { JqPluginDirective } from './libs/jq-plugin.directive';
import { InputSpaceValidateDirective } from '@app/shared/common/directives/input-space-validate/input-space-validate.directive';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BootstrapSelectPickerTitleComponent } from './components/bootstrap-select-picker/bootstrap-select-picker-title.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { OverlayLoaderComponent } from './components/overlay-loader/overlay-loader.component';
import { ToastrModule } from 'ngx-toastr';
import { BootstrapSelectPickerComponent } from './components/bootstrap-select-picker/bootstrap-select-picker.component';
import { DeletePopupComponent } from './components/delete-popup/delete-popup.component';
import { TaskDeleteConfirmComponent } from './components/task-delete-confirm/task-delete-confirm.component';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { SubscriptionTrialEndedModalComponent } from '@app/main/subscription-trial-ended-modal/subscription-trial-ended-modal.component';
import { SubscriptionBillingDetailModalComponent } from '@app/main/subscription-billing-detail-modal/subscription-billing-detail-modal.component';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { ErrorProcessingStripeModalComponent } from '@app/main/error-processing-stripe-modal/error-processing-stripe-modal.component';
import { SubscriptionSuccessModalComponent } from '@app/main/subscription-success-modal/subscription-success-modal.component';
import { ProfileModalComponent } from '@app/main/profile-modal/profile-modal.component';
import { EmailSignatureEditorComponent } from './email-signature/email-signature-editor.component';
import { EmailSignaturePasteDialogComponent } from './email-signature/email-signature-paste-dialog.component';
import { EmailSignaturePreviewComponent } from './email-signature/email-signature-preview.component';
import { ConnectedAppsModalComponent } from '@app/main/connected-apps-modal/connected-apps-modal.component';
import { DragulaModule } from 'ng2-dragula';
import { NgxSkeletonModule } from 'ngx-skeleton';
import { FileListComponent } from '@app/main/agenda/file/file-list.component';
import { CustomToastComponent } from './components/custom-toast/custom-toast.component';
import { PhoneNumberFormatPipe } from './pipes/phone-number-format.pipe';
import { NavigateSubFoldersModalComponent } from '@app/main/agenda/navigate-sub-folders-modal/navigate-sub-folders-modal.component';
import { FileSelectorComponent } from './components/file-selector/file-selector.component';
import { DoLaterComponent } from './components/do-later/do-later.component';
import { CopyOrMoveToPopupComponent } from './components/copy-or-move-to-popup/copy-or-move-to-popup.component';
import { FileIconComponent } from './components/file-icon/file-icon.component';
import { AttachmentIconComponent } from './components/file-icon/attachment-icon.component';
import { SubscriptionAdjustUsersModalComponent } from '@app/main/subscription-adjust-users-modal/subscription-adjust-users-modal.component';
import { InfoPopupComponent } from './components/info-popup/info-popup.component';
import { DateRangePickerComponent } from './components/date-range-picker/date-range-picker.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { ProjectDetailsPanelComponent } from '@app/main/agenda/project/project-details-panel/project-details-panel.component';
import { AddressEditorComponent } from './components/address-editor/address-editor.component';
import { AdminConsentModalComponent } from './components/admin-consent-modal/admin-consent-modal.component';
import { SelectSharepointSiteModalComponent } from './components/select-sharepoint-site-modal/select-sharepoint-site-modal.component';
import { TiptapEditorComponent } from './tiptap-editor';
import { WriteWithAiCalloutComponent, WriteWithAiOverlayComponent } from './write-with-ai';
import { TaskEmailBarComponent } from './task-email-bar/task-email-bar.component';
import { TaskProjectReviewComponent } from './task-project-review/task-project-review.component';
import { TaskClientReviewComponent } from './task-client-review/task-client-review.component';
import { TaskDateNudgeComponent } from './task-date-nudge/task-date-nudge.component';
import { ActionToastComponent } from './action-toast/action-toast.component';
import { ImageUploadModalComponent } from './components/image-upload-modal/image-upload-modal.component';
import { ImageLightboxComponent } from './components/image-lightbox/image-lightbox.component';
import { VfModalShellComponent } from './components/modal-shell/vf-modal-shell.component';
import { ToastModule } from '@syncfusion/ej2-angular-notifications';
import {
    AddModule, ArrowsHorizontalModule, ArrowUpRightModule, BuildingModule, CalendarModule, CarouselHorizontalModule, CatalogModule, CheckboxCheckedModule, CheckmarkOutlineModule, ChevronLeftModule, CloseModule, CloseOutlineModule, CloudModule, CloudUploadModule, CopyFileModule, CopyModule, DocumentBlankModule,
    DraggableModule, EditModule, FilterModule, FilterRemoveModule, FolderAddModule, FolderDetailsModule, FolderModule, FoldersModule, HomeModule, IbmSecurityModule, ImageModule, LinkModule, ListBoxesModule, LockedModule, MagicWandModule, MaximizeModule, MinimizeModule, MovementModule, 
    OverflowMenuHorizontalModule, OverflowMenuVerticalModule, PinModule, PortfolioModule, RenewModule, ResetModule, SettingsModule, ShareModule, SunModule, TagModule, TemplateModule, TimeModule, TrashCanModule, 
    UnlinkModule, UploadModule,  UserAvatarFilledAltModule, DataTableModule, DocumentPdfModule,
    UserModule, UserMultipleModule, ViewModule, WarningModule, WikisModule,
    ChevronRightModule, CollapseCategoriesModule
} from '@carbon/icons-angular';
import { FileSelectorMobileComponent } from './components/file-selector/file-selector-mobile.component';
import { DropDownListAllModule } from '@syncfusion/ej2-angular-dropdowns';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { TreeDragDropService } from 'primeng/api';
import { TreeModule } from 'primeng/tree';
import { StyleClassModule } from 'primeng/styleclass';
import { LucideAngularModule, Folder, Calendar, X, ChevronLeft, ChevronRight, Globe, Search, CheckCircle, Check, HardDrive, Image, FileText, ShieldCheck, Lock, Tag, CreditCard, ArrowLeftRight, FolderInput, Copy, ClipboardPaste, ChevronDown, Trash2, Upload, Plus, ExternalLink, CheckSquare, Star, Sun, Ellipsis, Bold, Italic, Underline, Strikethrough, List, ListOrdered, ListChecks, IndentIncrease, IndentDecrease, Palette, Highlighter, Link2, Type, Edit, Sparkles, Undo2, Redo2, Table, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, PanelTop, Combine, Trash, Send, PencilLine, Repeat, TriangleAlert, User, Users, Circle, Heart, LifeBuoy, Rocket, ThumbsUp, Phone, Monitor, Smartphone, CornerUpRight, ChevronUp, CircleCheck, Layers, GripVertical, Pencil, Info, Settings, Briefcase, Hash, Flag, MapPin, DollarSign, AlignCenter, AlignLeft, AlignRight, RotateCcw, Building2, Link, CalendarRange, ToggleLeft, ArrowUpRight, RefreshCw, Unplug } from 'lucide-angular';
import { GetHelpPanelComponent } from './get-help/get-help-panel/get-help-panel.component';
@NgModule({
    imports: [
        ngCommon.CommonModule,
        FormsModule,
        ModalModule.forRoot(),
        UtilsModule,
        BsDatepickerModule.forRoot(),
        BsDropdownModule.forRoot(),
        CommonModule,
        TableModule,
        PaginatorModule,
        DragulaModule.forRoot(),
        TypeaheadModule.forRoot(),
        TooltipModule,
        ToastrModule.forRoot({
            toastComponent: CustomToastComponent
        }),
        AutosizeModule,
        PerfectScrollbarModule,
        NgxSkeletonModule,
        FoldersModule,
        AddModule,
        FilterModule,
        FilterRemoveModule,
        ResetModule,
        CloseModule,
        OverflowMenuVerticalModule,
        OverflowMenuHorizontalModule,
        DraggableModule,
        LockedModule,
        CopyFileModule,
        TrashCanModule,
        PinModule,
        ArrowsHorizontalModule,
        MinimizeModule,
        MaximizeModule,
        SettingsModule,
        CheckboxCheckedModule,
        ArrowUpRightModule,
        SunModule,
        TimeModule,
        MovementModule,
        ShareModule,
        EditModule,
        WikisModule,
        DataTableModule,
        DocumentPdfModule,
        UserModule,
        DocumentBlankModule,
        CatalogModule,
        ImageModule,
        LinkModule,
        CopyModule,
        CheckmarkOutlineModule,
        CloudModule,
        CalendarModule,
        RenewModule,
        TagModule,
        MagicWandModule,
        ChevronLeftModule,
        ChevronRightModule,
        CloudUploadModule,
        UploadModule,
        FolderAddModule,
        CarouselHorizontalModule,
        ListBoxesModule,
        BuildingModule,
        FolderModule,
        FolderDetailsModule,
        WarningModule,
        ViewModule,
        CloseOutlineModule,
        UserMultipleModule,
        UserAvatarFilledAltModule,
        TemplateModule,
        IbmSecurityModule,
        UnlinkModule,
        PortfolioModule,
        DropDownListAllModule,
        ToastModule,
        NgxDropzoneModule,
        TreeModule,
        StyleClassModule,
        HomeModule,
        CollapseCategoriesModule,
        AgGridModule,
        LucideAngularModule.pick({ Folder, Calendar, X, ChevronLeft, ChevronRight, Globe, Search, CheckCircle, Check, HardDrive, Image, FileText, ShieldCheck, Lock, Tag, CreditCard, ArrowLeftRight, FolderInput, Copy, ClipboardPaste, ChevronDown, Trash2, Upload, Plus, ExternalLink, CheckSquare, Star, Sun, Ellipsis, Bold, Italic, Underline, Strikethrough, List, ListOrdered, ListChecks, IndentIncrease, IndentDecrease, Palette, Highlighter, Link2, Type, Edit, Sparkles, Undo2, Redo2, Table, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, PanelTop, Combine, Trash, Send, PencilLine, Repeat, TriangleAlert, User, Users, Circle, Heart, LifeBuoy, Rocket, ThumbsUp, Phone, Monitor, Smartphone, CornerUpRight, ChevronUp, CircleCheck, Layers, GripVertical, Pencil, Info, Settings, Briefcase, Hash, Flag, MapPin, DollarSign, AlignCenter, AlignLeft, AlignRight, RotateCcw, Building2, Link, CalendarRange, ToggleLeft, ArrowUpRight, RefreshCw, Unplug })
    ],
    declarations: [
        JqPluginDirective,
        InputSpaceValidateDirective,
        BootstrapSelectPickerTitleComponent,
        BootstrapSelectPickerComponent,
        SpinnerComponent,
        OverlayLoaderComponent,
        DeletePopupComponent,
        TaskDeleteConfirmComponent,
        FileSelectorComponent,
        FileSelectorMobileComponent,
        DateAgoPipe,
        SubscriptionTrialEndedModalComponent,
        SubscriptionBillingDetailModalComponent,
        ErrorProcessingStripeModalComponent,
        SubscriptionSuccessModalComponent,
        ProfileModalComponent,
        EmailSignatureEditorComponent,
        EmailSignaturePasteDialogComponent,
        EmailSignaturePreviewComponent,
        FileListComponent,
        CustomToastComponent,
        PhoneNumberFormatPipe,
        NavigateSubFoldersModalComponent,
        DoLaterComponent,
        FileIconComponent,
        AttachmentIconComponent,
        CopyOrMoveToPopupComponent,
        SubscriptionAdjustUsersModalComponent,
        InfoPopupComponent,
        DateRangePickerComponent,
        DatePickerComponent,
        AdminConsentModalComponent,
        SelectSharepointSiteModalComponent,
        TiptapEditorComponent,
        WriteWithAiCalloutComponent,
        WriteWithAiOverlayComponent,
        ImageUploadModalComponent,
        ImageLightboxComponent,
        ProjectDetailsPanelComponent,
        AddressEditorComponent,
        TaskEmailBarComponent,
        TaskProjectReviewComponent,
        TaskClientReviewComponent,
        TaskDateNudgeComponent,
        ActionToastComponent,
        ConnectedAppsModalComponent,
        VfModalShellComponent,
        GetHelpPanelComponent
    ],
    exports: [
        GetHelpPanelComponent,
        JqPluginDirective,
        InputSpaceValidateDirective,
        BootstrapSelectPickerTitleComponent,
        BootstrapSelectPickerComponent,
        SpinnerComponent,
        OverlayLoaderComponent,
        DeletePopupComponent,
        TaskDeleteConfirmComponent,
        FileSelectorComponent,
        FileSelectorMobileComponent,
        DateAgoPipe,
        SubscriptionTrialEndedModalComponent,
        SubscriptionBillingDetailModalComponent,
        ErrorProcessingStripeModalComponent,
        SubscriptionSuccessModalComponent,
        ProfileModalComponent,
        EmailSignatureEditorComponent,
        EmailSignaturePasteDialogComponent,
        EmailSignaturePreviewComponent,
        ConnectedAppsModalComponent,
        CustomToastComponent,
        FileListComponent,
        PhoneNumberFormatPipe,
        NavigateSubFoldersModalComponent,
        DoLaterComponent,
        FileIconComponent,
        AttachmentIconComponent,
        CopyOrMoveToPopupComponent,
        SubscriptionAdjustUsersModalComponent,
        InfoPopupComponent,
        DateRangePickerComponent,
        DatePickerComponent,
        AdminConsentModalComponent,
        SelectSharepointSiteModalComponent,
        TiptapEditorComponent,
        WriteWithAiCalloutComponent,
        WriteWithAiOverlayComponent,
        ImageUploadModalComponent,
        ImageLightboxComponent,
        ProjectDetailsPanelComponent,
        AddressEditorComponent,
        TaskEmailBarComponent,
        TaskProjectReviewComponent,
        TaskClientReviewComponent,
        TaskDateNudgeComponent,
        ActionToastComponent,
        VfModalShellComponent
    ],
    providers: [
        AppLocalizationService,
        AppNavigationService,
        TreeDragDropService
    ]
})
export class AppCommonModule {
    static forRoot(): ModuleWithProviders<AppCommonModule> {
        return {
            ngModule: AppCommonModule,
            providers: [
                AppAuthService,
                AppRouteGuard
            ]
        };
    }
}
